import axios from 'axios'
import { useAtom } from "jotai"
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'

import type {
    FormMutationOptions,
    PublicBinInstruction,
    PublicBinInstructionFormInput,
} from '@/types'
import { selectedMunicipalityIdAtom } from "@/state"

export function usePublicBinInstructions(municipalityId) {
    return useQuery({
        queryKey: ['publicBinInstructions', municipalityId],
        queryFn: async () => {
            const { data } : { data: Array<PublicBinInstruction> } = await axios.get(
                '/api/public_bin_instruction/list?municipality_id=' + municipalityId,
            )
            return data
        },
    })
}

export const useCreatePublicBinInstruction = (options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const [municipalityId] = useAtom(selectedMunicipalityIdAtom)

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: PublicBinInstructionFormInput) => {            
            return axios.post(`/api/public_bin_instruction/create?municipality_id=${municipalityId}`,
                {description: data.description}
            )
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['publicBinInstructions'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        }
    })
}

export const useUpdatePublicBinInstruction = (publicBinInstructionId, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: PublicBinInstructionFormInput) => {
            return axios.put(`/api/public_bin_instruction/update?id=${publicBinInstructionId}`,
                {description: data.description}
            )
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['publicBinInstructions'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        }
    })
}
